/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Observable, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar, MatLegacySnackBarRef as MatSnackBarRef } from '@angular/material/legacy-snack-bar';
import { catchError } from 'rxjs/operators';
import moment  from 'moment';

// local imports.
import { IconSnackBarComponent } from '@core/components/snack-bar/snack-bar.component';
import { DEFAULT_DATE_FORMAT, SNACKBAR_NOTIFICATION_TYPE } from '@core/constants';
import { ApiResponse } from '@core/models';
import { ApiService } from '../../services/api.service';

const FORM_CONTROL_NAMES = {
  email: 'Email',
  phone: 'Phone Number',
  phoneNumber: 'Phone Number',
  contactName: 'Contact Name',
  // location related fields
  city: 'City',
  state: 'State',
  zipCode: 'Zip Code',
  // customer related fields
  commissionRate: 'Commission Rate',
};

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  // public properties

  formValidityResponses: boolean[] = [];
  firstInvalidFormName = '';
  formControlNames = FORM_CONTROL_NAMES;
  timezone: string;

  // private properties
  private production: boolean;
  private staging: boolean;
  private qa: boolean;
  private dev: boolean;

  constructor(private apiService: ApiService, private http: HttpClient, private snackBar: MatSnackBar) {
    this.production = true;
    this.staging = false;
  }

  static parseQueryParams(queryString): any {
    if (!queryString) {
      return {};
    }
    const params = {};

    queryString.split('&').forEach((queryParam) => {
      const [key, value] = queryParam.split('=');
      return (params[key] = value);
    });

    return params;
  }

  static sleep(ms: number): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  getSubdomain(): string {
    if (this.dev) {
      return 'dev';
    }
    if (this.qa) {
      return 'qa';
    }
    if (this.production) {
      return 'prod';
    }
    if (this.staging) {
      return 'stage';
    }

    return '';
  }

  browserTimezone(): string {
    return this.timezone;
  }

  isProduction(): boolean {
    return this.production;
  }

  isStaging(): boolean {
    return this.staging;
  }

  isQa(): boolean {
    return this.qa;
  }

  isDev(): boolean {
    return this.dev;
  }

  dataToString(data): string {
    const momentData = moment(data);
    return momentData && momentData.isValid() ? momentData.format(DEFAULT_DATE_FORMAT) : '';
  }

  dataToStringWithFormat(data: any, format: string): string {
    const momentData = moment(data);
    return momentData && momentData.isValid() ? momentData.format(format) : '';
  }

  loadUsaStates(): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.utils}/countries-with-states`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  checkTokenValidity(): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi.replace('v1', '')}utils/health`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  showNotification(
    message: string,
    type = SNACKBAR_NOTIFICATION_TYPE.SUCCESS,
    duration?
  ): MatSnackBarRef<IconSnackBarComponent> {
    return this.snackBar.openFromComponent(IconSnackBarComponent, {
      data: {
        message,
        type,
      },
      verticalPosition: 'top',
      horizontalPosition: 'right',
      duration: duration || 5000,
      panelClass: ['zmt-snackbar-container', type],
    });
  }

  showMultipleNotifications(
    notifications: { message: string; type: SNACKBAR_NOTIFICATION_TYPE.SUCCESS; duration?: number }[]
  ): void {
    notifications.forEach((notification, index) => {
      const { message, type = SNACKBAR_NOTIFICATION_TYPE.SUCCESS, duration = 1500 } = notification;
      setTimeout(() => this.showNotification(message, type, duration), index * (duration + 500));
    });
  }

  /**
   * Format the price from number to string ( 7700 => $77.00 )
   */
  formatPrice(price, showSymbol = true): string {
    const newValue = (price / 100).toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,');
    return showSymbol ? `$${newValue}` : newValue;
  }

  /**
   * Format the price from string to number ( $77.00 => 7700 )
   */
  unformatPrice(price: string): number {
    if (!price) {
      return 0;
    }

    price = price.toString().replace(/[^0-9-]+/g, '');
    return Number(price);
  }

  // Mark All required fields asTouched
  markFormFieldsAsTouched(group: UntypedFormGroup | UntypedFormArray): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key];
      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        this.markFormFieldsAsTouched(abstractControl);
      } else if (abstractControl.invalid) {
        abstractControl.markAsTouched();
      }
    });
  }

  // Mark All required fields asDirty
  markFormFieldsAsDirty(group: UntypedFormGroup | UntypedFormArray): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key];
      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        this.markFormFieldsAsDirty(abstractControl);
      } else {
        if (abstractControl.invalid) {
          abstractControl.markAsDirty();
        }
      }
    });
  }

  checkFormValidity(group: UntypedFormGroup | UntypedFormArray, formControlNames?: any): boolean {
    const customTextNotification = typeof formControlNames !== 'undefined';
    formControlNames = customTextNotification ? formControlNames : {};
    this.formControlNames = Object.assign(FORM_CONTROL_NAMES, formControlNames);
    this.formValidityResponses = [];
    this.firstInvalidFormName = '';

    this.checkForm(group);
    if (this.formValidityResponses.includes(false)) {
      this.showNotification(
        customTextNotification
          ? this.formControlNames[this.firstInvalidFormName]
          : `Invalid ${this.formControlNames[this.firstInvalidFormName]}`,
        SNACKBAR_NOTIFICATION_TYPE.ERROR
      );
      return false;
    }
    return true;
  }

  /**
   * trimString
   */
  trimString(text, replaceCharacters = '', stripPhoneNumber = false): string {
    if (!text) {
      return text;
    }
    // removes whitespace from both ends of the text
    text = text.toString().trim();

    // if the text is phone number format, then strip all non-numeric characters from it
    if (stripPhoneNumber && this.isPhoneNumberFormat(text)) {
      text = text.replace(/\D/g, '');
    }
    // strip all replaceCharacters from text
    if (replaceCharacters) {
      text = text.replace(new RegExp(`[${replaceCharacters}]`, 'g'), '');
    }

    return text;
  }

  /**
   * Check if is Usa phone number format
   */
  isPhoneNumberFormat(text): boolean {
    const phoneRegex = /^(\+)?(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[\0-9]{3}[\s\-]?[0-9]{4}$/;
    return phoneRegex.test(text);
  }

  checkForm(group: UntypedFormGroup | UntypedFormArray): boolean {
    let valid = true;
    if (!this.formValidityResponses.includes(false)) {
      Object.keys(group.controls).forEach((key: string) => {
        const abstractControl = group.controls[key];
        if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
          this.checkForm(abstractControl);
        } else {
          if (abstractControl.invalid) {
            valid = false;
            if (!this.firstInvalidFormName) {
              this.firstInvalidFormName = key;
            }
            this.formValidityResponses.push(false);
            abstractControl.markAsTouched();
          }
        }
      });

      // run validators on formGroup
      if (group.validator) {
        const groupErrors = group.validator(group);
        if (groupErrors) {
          Object.keys(groupErrors).forEach((key: string) => {
            valid = false;
            if (!this.firstInvalidFormName) {
              this.firstInvalidFormName = key;
            }
            this.formValidityResponses.push(false);
          });
        }
      }
    }
    return valid;
  }

  public toCamelCase(s: string): string {
    return s.replace(/([-_][a-z])/gi, ($1) => {
      return $1.toUpperCase().replace('-', '').replace('_', '');
    });
  }

  public toProperCase(s: string): string {
    return s.replace('_', ' ').replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
}
